import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import SyncIcon from '@mui/icons-material/Sync';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { useQueryClient } from '@tanstack/react-query';

import { makeAuthenticatedApiCall } from '../../apiHelper';
import { useAgencyFeatureFlag } from '../../hooks/useAgencyFeatureFlag';
import { useCGList, useClientFees } from '../../hooks/useApiQueries';
import { type InFlightUploadRow, useInFlightUploads } from '../../hooks/useInFlightUploads';
import useSearchHandler from '../../hooks/useSearchHandler';
import { formatDateToDDMMYYYY } from '../../utils';
import { useAuth } from '../AuthContext';
import { useBackgroundUploads } from '../BackgroundUploads/BackgroundUploadsProvider';
import CommentsManager from '../Comments/CommentsManager';
import SubmitCTVO from '../DealsList/DealStageActions/SubmitCTVO';
import ActionsRequiredAccordion, { ActionItem, ActionType } from './ActionsRequiredAccordion';
import { CGStatusInsightChip } from './CGStatusInsightChip';
import { ComplementBadge } from './Complement';
import PaymentModal from './PaymentModal';

interface CGTableProps {
  title: string;
  cgList: any[];
  onRefresh: () => Promise<void>;
  scrollTarget?: { registrationId: number; nonce: number } | null;
}

// Composant pour afficher le prix à payer avec les frais dynamiques
const CGAgencyPriceDisplay = ({
  clientId,
  taxDataEstim,
}: {
  clientId: string | null | undefined;
  taxDataEstim: any;
}) => {
  const { data: clientFeesData, isLoading } = useClientFees(clientId);

  // Récupérer la commission Skipcar (cgFee) depuis l'API ou utiliser 10€ par défaut
  const skipcarFee = (clientFeesData as any)?.data?.cgFee ?? 10;

  if (isLoading) {
    return <CircularProgress size={12} />;
  }

  // La taxe carte grise est exonérée de TVA (0%), seule la commission Skipcar est soumise à 20% de TVA
  const priceToPayTTC = (taxDataEstim?.taxeTotale ?? 0) + skipcarFee * 1.2;

  return <>{priceToPayTTC.toFixed(2)}</>;
};

type Order = 'asc' | 'desc';

interface HeadCell {
  id: string;
  label: string;
  numeric: boolean;
}

const CGTable = ({ title, cgList, onRefresh, scrollTarget }: CGTableProps) => {
  const { t } = useTranslation('common');
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<string>('RegistrationID');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [openPaymentModal, setOpenPaymentModal] = useState<boolean>(false);
  const [selectedInvoice, setSelectedInvoice] = useState<string>('');
  const [price, setPrice] = useState<number>(0);
  const { currentUser } = useAuth();
  const userRole = currentUser?.data?.data?.role;
  const userPipedrive = currentUser?.data?.data?.idPipedrive ?? 188;
  const navigate = useNavigate();

  useEffect(() => {
    setPage(0);
  }, [cgList]);

  const headCells: readonly HeadCell[] = [
    { id: 'RegPlate', label: 'Immat', numeric: false },
    { id: 'RegistrationID', label: 'ID', numeric: false },
    { id: 'SellerBuyer', label: 'Vendeur / Acheteur', numeric: false },
    { id: 'CreatedAt', label: 'Date de création', numeric: false },
    { id: 'PurchaseDateTime', label: 'Date de vente', numeric: false },
    { id: 'Comments', label: 'Commentaires', numeric: false },
    { id: 'Actions', label: 'Actions', numeric: false },
  ];

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  function getComparator<Key extends keyof any>(
    order: Order,
    orderBy: Key,
  ): (a: { [key in Key]: number | string }, b: { [key in Key]: number | string }) => number {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
    if (b[orderBy] < a[orderBy]) return -1;
    if (b[orderBy] > a[orderBy]) return 1;
    return 0;
  }

  const handleOpenPaymentModal = async (invoice: string, price: number, clientId: string | null) => {
    setSelectedInvoice(invoice);

    // Récupérer les frais dynamiques pour ce client
    const skipcarFee = await (async () => {
      if (!clientId) {
        return 10; // Valeur par défaut
      }

      try {
        const response: any = await makeAuthenticatedApiCall('get', `/api/rfa/client-fees/${clientId}`);
        return response.data?.data?.cgFee ?? 10;
      } catch (error) {
        console.error('Erreur lors de la récupération des frais:', error);
        return 10; // Fallback en cas d'erreur
      }
    })();

    // La taxe carte grise est exonérée de TVA (0%), seule la commission Skipcar est soumise à 20% de TVA
    setPrice(price + skipcarFee * 1.2);
    setOpenPaymentModal(true);
  };

  const handleClosePaymentModal = () => {
    setOpenPaymentModal(false);
  };

  function handleCGClick(registrationId: number) {
    navigate(`/form-cg?registrationId=${registrationId}`);
  }

  async function handleInvoiceClick(dealId: number) {
    try {
      const response = await makeAuthenticatedApiCall<{ invoice_url_firebase: string }>(
        'get',
        `api/qonto/deals/f/${dealId}`,
      );
      if (response.data.invoice_url_firebase) {
        window.open(response.data.invoice_url_firebase, '_blank');
      } else {
        console.error('No invoice URL found');
        return false;
      }
    } catch (error) {
      console.error('Error getting invoice:', error);
    }
  }

  const sortedRows = useMemo(() => {
    return [...cgList].sort(getComparator(order, orderBy));
  }, [cgList, order, orderBy]);

  useEffect(() => {
    if (!scrollTarget) return;
    const targetIndex = sortedRows.findIndex((cg: any) => cg.RegistrationID === scrollTarget.registrationId);
    if (targetIndex === -1) return;
    const targetPage = Math.floor(targetIndex / rowsPerPage);
    if (targetPage !== page) setPage(targetPage);
    window.setTimeout(() => {
      const el = document.getElementById(`cg-row-${scrollTarget.registrationId}`);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('cg-row-highlight');
      window.setTimeout(() => el.classList.remove('cg-row-highlight'), 1600);
    }, 60);
  }, [scrollTarget, sortedRows, rowsPerPage, page]);

  return (
    <Box sx={{ width: '100%', mb: 4 }}>
      <Typography variant="body1" sx={{ mb: 2, fontWeight: 'bold', fontSize: '18px' }}>
        {title}
      </Typography>
      <Paper sx={{ width: '100%', mb: 2 }}>
        {sortedRows.length === 0 ? (
          <Typography variant="body2" sx={{ p: 2, textAlign: 'center' }}>
            Pas de carte grise dans cette catégorie
          </Typography>
        ) : (
          <>
            <Table stickyHeader>
              <TableHead>
                <TableRow sx={{ background: 'black' }}>
                  {headCells.map((headCell) => (
                    <TableCell
                      key={headCell.id}
                      align={headCell.numeric ? 'right' : 'left'}
                      sortDirection={orderBy === headCell.id ? order : false}
                      sx={{
                        fontWeight: 'bold',
                        background: 'black',
                        ...(headCell.id === 'Actions' && { width: 155 }),
                      }}
                    >
                      <TableSortLabel
                        active={orderBy === headCell.id}
                        direction={orderBy === headCell.id ? order : 'asc'}
                        onClick={() => handleRequestSort(headCell.id)}
                        sx={{ background: 'black' }}
                      >
                        {headCell.label}
                        {orderBy === headCell.id ? (
                          <Box component="span" sx={visuallyHidden}>
                            {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                          </Box>
                        ) : null}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((cg: any) => (
                  <TableRow
                    hover
                    key={`${cg.RegistrationID}-${cg.deal_id}`}
                    id={`cg-row-${cg.RegistrationID}`}
                    sx={{
                      transition: 'background-color 600ms ease',
                      '&.cg-row-highlight': { backgroundColor: 'rgba(245, 194, 0, 0.18)' },
                    }}
                  >
                    <TableCell sx={{ borderBottom: '2px solid' }}>
                      <Link href={`/form-cg?registrationId=${cg.RegistrationID}`}>{cg.RegPlate}</Link>
                      {/* Chip d'insight contextuel : pas de répétition du nom
                          du statut (déjà donné par le titre du bloc parent),
                          mais une info utile à l'agence (score IA, nombre de
                          pièces, montant à payer, ETA démarches, etc.) avec
                          l'emoji magique ✨ comme signal IA Skipcar. */}
                      <CGStatusInsightChip cg={cg} />
                      <ComplementBadge cg={cg} onChanged={onRefresh} />
                    </TableCell>
                    <TableCell align="right" sx={{ borderBottom: '2px solid' }}>
                      {cg.RegistrationID}
                    </TableCell>
                    <TableCell sx={{ borderBottom: '2px solid' }}>
                      <Typography fontSize="14px" variant="body2">
                        {`${cg.SellerFirstName ?? ''} ${cg.SellerLastName ?? ''}`} {`${cg.SellerCompanyName ?? ''}`}
                      </Typography>
                      <Typography fontSize="14px" variant="body2" sx={{ mt: 1 }}>
                        {`${cg.BuyerFirstName ?? ''} ${cg.BuyerLastName ?? ''}`} {`${cg.BuyerCompanyName ?? ''}`}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ borderBottom: '2px solid' }}>
                      {formatDateToDDMMYYYY(cg.CreatedAt ?? cg.UpdatedAt) ?? ''}
                    </TableCell>
                    <TableCell sx={{ borderBottom: '2px solid' }}>
                      {formatDateToDDMMYYYY(cg.PurchaseDateTime ?? '')}
                    </TableCell>
                    <TableCell sx={{ minWidth: '200px', borderBottom: '2px solid' }}>
                      <CommentsManager
                        registrationId={cg.RegistrationID}
                        internalComments={cg.Comments || []}
                        userRole={userRole}
                        type="cg"
                        refetch={async () => await onRefresh()}
                        maxWidth={200}
                        regPlate={cg.RegPlate}
                      />
                    </TableCell>
                    <TableCell align="center" sx={{ borderBottom: '2px solid' }}>
                      <Stack direction="column" spacing={1} alignItems="center">
                        {cg.StageID === 46 && (
                          <Button
                            sx={{
                              fontSize: 14,
                              background: '#FF6060',
                              opacity: 1,
                              fontWeight: 600,
                              width: 152,
                            }}
                            className="dealActionButton"
                            onClick={() =>
                              handleOpenPaymentModal(cg.InvoiceNumber, cg?.TaxDataEstim?.taxeTotale ?? 0, cg.ClientID)
                            }
                          >
                            Payer
                          </Button>
                        )}
                        {cg.StageID === 46 && (
                          <>
                            <Typography
                              variant="body2"
                              sx={{ marginTop: '0 !important', fontSize: '12px', fontWeight: 600 }}
                            >
                              Référence pour le virement : {cg.InvoiceNumber}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ marginTop: '0 !important', fontSize: '12px', fontWeight: 600 }}
                            >
                              Prix à payer :{' '}
                              <CGAgencyPriceDisplay clientId={cg.ClientID} taxDataEstim={cg.TaxDataEstim} /> € TTC
                            </Typography>
                          </>
                        )}
                        <Button
                          sx={{
                            fontSize: 14,
                            width: 152,
                            display: cg.InvoiceStatus ? 'block' : 'none',
                          }}
                          className="secondaryDealActionButton"
                          variant="contained"
                          onClick={() => handleInvoiceClick(cg.deal_id)}
                        >
                          Voir la facture
                        </Button>

                        <Typography variant="body2" sx={{ fontSize: '12px' }}>
                          {cg.InvoiceNumber} {cg.InvoiceStatus && `/ ${t(`invoiceStatus.${cg.InvoiceStatus}`)}`}
                        </Typography>

                        <Button
                          sx={{
                            fontSize: 14,
                            width: 152,
                          }}
                          className="secondaryDealActionButton"
                          variant="contained"
                          onClick={() => handleCGClick(cg.RegistrationID)}
                        >
                          Voir la demande
                        </Button>

                        {/* Carte grise validée par l'ANTS mais complément non réglé : le
                            récépissé n'est disponible qu'après le règlement (§10.10). */}
                        {cg.StageID === 48 && Number(cg.ComplementUnpaidCents ?? 0) > 0 ? (
                          <Typography variant="body2" sx={{ maxWidth: 200, textAlign: 'center', color: '#6d28d9' }}>
                            Le récépissé est disponible une fois le complément réglé.
                          </Typography>
                        ) : (
                          <SubmitCTVO
                            deal={cg}
                            pipelineLetter="f"
                            stageId={cg.StageID}
                            userRole={userRole}
                            onRefresh={onRefresh}
                          />
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 20, 50]}
              component="div"
              count={sortedRows.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Lignes par page"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            />
          </>
        )}
      </Paper>
      <PaymentModal
        open={openPaymentModal}
        onClose={handleClosePaymentModal}
        price={price}
        invoiceNumber={selectedInvoice}
      />
    </Box>
  );
};

/**
 * Section "En cours" : liste les uploads CG en arrière-plan (FormCGIA v2).
 * Source = /api/cg/in-flight, rafraîchi périodiquement + via WebSocket
 * (cf. useInFlightUploads). Les rows disparaissent automatiquement quand
 * l'upload est en `done` ET que le registration_id + deal_id sont renseignés
 * (filtré côté backend dans listByAgency).
 */
const InFlightSection = () => {
  const { uploads, isLoading } = useInFlightUploads();
  const { showTracker } = useBackgroundUploads();

  // Le titre du bloc est "Demandes en cours d'analyse" → on ne montre QUE les
  // rows réellement actives (queued / uploading / analyzing / submitting).
  // Les rows `done` restent visibles 5 min côté backend (pour permettre au
  // toast bottom-left d'afficher "Demande finalisée"), mais elles n'ont rien
  // à faire dans ce bloc — sinon l'agence voit "en cours d'analyse" sur un
  // dossier marqué Terminé, ce qui est contradictoire.
  const activeUploads = (uploads ?? []).filter(
    (u) =>
      u.state === 'queued' ||
      u.state === 'uploading' ||
      u.state === 'analyzing' ||
      u.state === 'submitting',
  );

  if (isLoading && activeUploads.length === 0) return null;
  if (activeUploads.length === 0) return null;

  return (
    <Paper
      onClick={showTracker}
      sx={{
        mb: 4,
        p: 2,
        backgroundColor: 'rgba(232, 244, 253, 1)',
        borderRadius: 2,
        cursor: 'pointer',
        transition: 'transform 120ms ease, box-shadow 120ms ease',
        '&:hover': {
          transform: 'translateY(-1px)',
          boxShadow: '0 4px 14px rgba(0,0,0,0.08)',
        },
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h6">Demandes en cours d'analyse</Typography>
        <Typography variant="caption" sx={{ color: '#1976d2', fontWeight: 600 }}>
          Cliquer pour réafficher le tracker →
        </Typography>
      </Stack>
      <Stack spacing={1}>
        {activeUploads.map((upload: InFlightUploadRow) => {
          const total = upload.files_total || 1;
          const progress = upload.files_uploaded + upload.files_analyzed;
          const pct = Math.min(100, Math.round((progress / (total * 2)) * 100));
          // Variant `indeterminate` pour tous les états actifs (queued / uploading
          // / analyzing / submitting) — le spinner tourne en continu pour signaler
          // "ça travaille". Sinon, sur une row stuck à 2/9 téléversés, le pct
          // figé à 11% donnait l'illusion d'un freeze côté UX.
          const isActive =
            upload.state === 'queued' ||
            upload.state === 'uploading' ||
            upload.state === 'analyzing' ||
            upload.state === 'submitting';
          const stateLabel: Record<string, string> = {
            queued: "En file d'attente",
            uploading: 'Téléversement…',
            analyzing: 'Analyse IA…',
            submitting: 'Finalisation…',
            done: 'Terminé',
            cancelled: 'Annulé',
            error: 'Erreur',
          };
          return (
            <Box
              key={upload.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 1.5,
                bgcolor: 'background.paper',
                borderRadius: 2,
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
              }}
            >
              <CircularProgress
                variant={isActive ? 'indeterminate' : 'determinate'}
                value={isActive ? undefined : pct}
                size={28}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  Dossier #{upload.id.slice(0, 8)} — {stateLabel[upload.state] ?? upload.state}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {upload.files_uploaded}/{upload.files_total} téléversés · {upload.files_analyzed}/{upload.files_total}{' '}
                  analysés
                  {upload.error_message ? ` · ${upload.error_message}` : ''}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Stack>
    </Paper>
  );
};

// Helpers ActionsRequiredAccordion (dev) — utilisés pour alimenter l'accordéon
// "Actions requises" en tête de la liste agence.
const buildActionDescription = (cg: any): string => {
  const buyerName = `${cg.BuyerFirstName ?? ''} ${cg.BuyerLastName ?? ''}`.trim();
  const buyerCompany = cg.BuyerCompanyName ?? '';
  const buyer = buyerCompany || buyerName || 'Acheteur inconnu';
  return `Acheteur : ${buyer}`;
};

const AgencyCGList = ({ orgId }: { orgId: number }) => {
  const { currentUser } = useAuth();
  const userRole = currentUser?.data?.data?.role;
  const userPipedrive = currentUser?.data?.data?.idPipedrive ?? 188;
  const [isLoading, setIsLoading] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  // Adapte le wording de l'empty state au flag d'agence : "✨ avec IA" vs legacy.
  // Pour la navigation, on cible /form-cg dans tous les cas — CGFormRouter
  // redirige automatiquement vers /form-cgia si le flag est activé.
  const { enabled: formCgiaEnabled } = useAgencyFeatureFlag('form_cgia');

  const [scrollTarget, setScrollTarget] = useState<{ registrationId: number; nonce: number } | null>(null);

  const { searchQuery, hasSearched, handleSearch, resetSearch } = useSearchHandler();

  // Fetch all CG data for the agency
  const {
    data,
    isLoading: isLoadingCG,
    refetch,
  } = useCGList(
    userPipedrive,
    userRole,
    1,
    1000, // Fetch all at once for grouping
    searchQuery,
    null,
    'R.RegistrationID',
    'DESC',
    false,
    null,
  );

  const handleRefresh = useCallback(async () => {
    setIsLoading(true);
    try {
      queryClient.invalidateQueries({ queryKey: ['useCGList'] });
      await refetch();
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [queryClient, refetch]);

  const cgList: any[] = data?.cgList || [];

  // Sections ISO avec les statuts canoniques cg_status — un bloc par statut.
  // L'ordre des filtres correspond à l'ordre d'affichage (action agence en
  // tête, puis en cours côté Skipcar, puis terminal). Chaque CG va dans UNE
  // seule section grâce aux exclusions mutuelles.
  //
  // Mapping cg_status ↔ cg_review_status (legacy) :
  //   awaiting_docs_and_payment → cg_review_status='awaiting_payment_and_docs'
  //   awaiting_payment          → cg_review_status='awaiting_payment'
  //   awaiting_docs             → cg_review_status='awaiting_docs'
  //   awaiting_review           → cg_review_status='pending_god_review'
  //   awaiting_misiv            → cg_review_status='god_approved' (sauf Stage 48)
  //   completed                 → StageID=48
  //   rejected                  → cg_review_status='god_rejected'
  //
  // Pour les CGs legacy v1 (cg_review_status='none'), on dérive depuis StageID
  // pour rester ISO avec l'agence (cf. deriveCgStatus côté backend).

  // 1. Paiement + pièces à déposer (One-time, double action attendue)
  //
  //    Lecture du `cg_status` canonique, comme les sections 3 et 4. Ce bucket
  //    lisait le tuple legacy `CGReviewStatus`, qui reste figé sur
  //    `awaiting_payment_and_docs` après l'encaissement : le dossier payé
  //    s'affichait ici ET en section 3 (« Pièces à déposer »), donc deux fois,
  //    avec deux actions dont une déjà faite — on réclamait un paiement déjà
  //    encaissé (retour François, sprint septembre). `deriveCgStatus` tranche
  //    déjà correctement : `awaiting_payment_and_docs` + facture réglée rend
  //    `awaiting_docs`, pas `awaiting_docs_and_payment`.
  //
  //    La garde `awaiting_reply` disparaît avec le tuple legacy : les statuts
  //    canoniques sont exclusifs par construction, un dossier ne peut pas être
  //    à la fois `awaiting_docs_and_payment` et `awaiting_reply`.
  // 0. Facture complémentaire (spécification CG agence v1.3, §10.10) : un
  //    complément PROPOSÉ attend l'accord de l'agence, et un complément à régler
  //    sur un dossier que l'ANTS a déjà validé bloque la carte grise. Les deux
  //    remontent en tête ; le dossier n'apparaît pas une seconde fois plus bas.
  const cgComplementAction = cgList.filter(
    (cg: any) =>
      Number(cg.ComplementProposeCents ?? 0) > 0 || (cg.StageID === 48 && Number(cg.ComplementUnpaidCents ?? 0) > 0),
  );
  const cgListHorsComplement = cgList.filter((cg: any) => !cgComplementAction.includes(cg));

  const cgPaymentAndDocs = cgListHorsComplement.filter((cg: any) => cg.cg_status === 'awaiting_docs_and_payment');

  // 2. En attente de paiement (paiement seul, docs OK ou pas demandés)
  //
  //    L'exclusion de `awaiting_reply` n'est pas cosmétique : ce bucket teste le
  //    tuple legacy (CGReviewStatus / StageID), pas le statut canonique. Un
  //    dossier Stage 46 dont la facture est réglée et sur lequel Skipcar a écrit
  //    sort en `awaiting_reply`, tout en satisfaisant `StageID === 46` ici : il
  //    apparaîtrait dans DEUX sections à la fois, « En attente de paiement » et
  //    « Skipcar attend votre réponse », avec deux actions contradictoires
  //    affichées à l'agence.
  const cgPending = cgListHorsComplement.filter(
    (cg: any) =>
      cg.cg_status !== 'awaiting_reply' &&
      (cg.CGReviewStatus === 'awaiting_payment' ||
        (cg.StageID === 46 && cg.CGReviewStatus !== 'awaiting_payment_and_docs')),
  );

  // 3. Pièces à déposer (docs manquants, paiement déjà OK ou pas demandé)
  //    Single source of truth = `cg_status === 'awaiting_docs'` (canonique).
  //    Le backend (deriveCgStatus + syncCgStatus) dérive ce statut depuis
  //    l'union de :
  //      - cg_review_status='awaiting_docs' (v2 explicite)
  //      - Stage 47 + un commentaire externe god/arbitrator non répondu par
  //        l'agence (legacy v1)
  //    + transition automatique à chaque insert dans external_comments.
  //    Plus de filtre dérivé brittle ici — on lit cg_status directement.
  const cgWaitingDocuments = cgListHorsComplement.filter((cg: any) => cg.cg_status === 'awaiting_docs');

  // 3 bis. Réponse attendue (`awaiting_reply`) : Skipcar a écrit sans réclamer
  //    de pièce. Section distincte et non fusionnée avec la précédente : envoyer
  //    ces dossiers vers l'écran de dépôt affichait « Aucune pièce
  //    complémentaire demandée », l'agence n'avait rien à y faire et le dossier
  //    n'en sortait jamais. Ici on la renvoie vers la discussion.
  //    Cette section DOIT exister : sans elle, les dossiers concernés
  //    disparaîtraient de l'écran de l'agence, qui ne saurait plus qu'on
  //    l'attend (le défaut corrigé en juillet, EQ-598-TE).
  const cgWaitingReply = cgListHorsComplement.filter((cg: any) => cg.cg_status === 'awaiting_reply');

  // 4. En review Skipcar (l'équipe vérifie le dossier avant envoi ANTS).
  //    Lecture du `cg_status` canonique : couvre correctement le cas Stage 46
  //    + cg_review_status='pending_god_review' (paiement pas encore réglé)
  //    qui ne doit PAS apparaître ici — deriveCgStatus le route vers
  //    `awaiting_payment` tant que la facture n'est pas payée.
  const cgReview = cgListHorsComplement.filter((cg: any) => cg.cg_status === 'awaiting_review');

  // 5. Démarches en cours (envoyé à l'ANTS via MISIV, attente du retour) :
  //    god_approved v2 hors Stage 48, OU Stage 47 propre legacy
  //    (pas d'ext comments, cg_review_status='none').
  const cgMisiv = cgListHorsComplement.filter((cg: any) => {
    if (cg.StageID === 48) return false;
    if (cg.CGReviewStatus === 'god_approved') return true;
    // Fallback legacy : Stage 47 propre sans statut v2
    return cg.StageID === 47 && !cg.hasExternalComments && (cg.CGReviewStatus === 'none' || !cg.CGReviewStatus);
  });

  // 6. Démarches finalisées (terminal — CG livrée à l'agence)
  const cgCompleted = cgListHorsComplement.filter((cg: any) => cg.StageID === 48);

  // 7. Rejetées (terminal — dossier refusé par Skipcar)
  const cgRejected = cgListHorsComplement.filter((cg: any) => cg.CGReviewStatus === 'god_rejected');

  // ── Accordéon "Actions requises" (composant venant de dev) ──
  // Alimenté depuis MES filtres v2 plutôt que le tuple Pipedrive seul.
  // Une CG en `awaiting_payment_and_docs` génère 2 items (1 payment + 1
  // documents) — sémantiquement juste, mais elle apparaîtra 2 fois dans
  // l'accordéon. Acceptable pour le MVP, on pourra ajouter 'payment_and_documents'
  // au type ActionType plus tard si besoin.
  const actionItems: ActionItem[] = useMemo(() => {
    const items: ActionItem[] = [];
    const base = (cg: any) => ({
      registrationId: cg.RegistrationID,
      regPlate: cg.RegPlate,
      description: buildActionDescription(cg),
      raw: cg,
    });
    cgPending.forEach((cg) => items.push({ ...base(cg), type: 'payment' as ActionType }));
    cgWaitingDocuments.forEach((cg) => items.push({ ...base(cg), type: 'documents' as ActionType }));
    cgWaitingReply.forEach((cg) => items.push({ ...base(cg), type: 'documents' as ActionType }));
    cgPaymentAndDocs.forEach((cg) => {
      items.push({ ...base(cg), type: 'payment' as ActionType });
      items.push({ ...base(cg), type: 'documents' as ActionType });
    });
    return items;
  }, [cgPending, cgWaitingDocuments, cgWaitingReply, cgPaymentAndDocs]);

  const handleActionClick = useCallback((item: ActionItem) => {
    setScrollTarget({ registrationId: item.registrationId, nonce: Date.now() });
  }, []);

  const filterCG = (cgList: any[]) => {
    if (!searchQuery) return cgList;

    const query = searchQuery.toLowerCase();
    return cgList.filter((cg) => {
      return (
        cg.RegPlate?.toLowerCase().includes(query) ||
        cg.RegistrationID?.toString().includes(query) ||
        cg.SellerFirstName?.toLowerCase().includes(query) ||
        cg.SellerLastName?.toLowerCase().includes(query) ||
        cg.BuyerFirstName?.toLowerCase().includes(query) ||
        cg.BuyerLastName?.toLowerCase().includes(query)
      );
    });
  };

  const allCGFiltered = hasSearched ? filterCG(cgList) : [];

  const handleSearchChange = (value: string) => {
    if (!value.trim()) {
      resetSearch(refetch);
    } else {
      handleSearch(value, refetch);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h4" gutterBottom sx={{ marginTop: 0 }}>
        Vos cartes grises
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Liste des cartes grises de votre agence.
      </Typography>
      {/* Toolbar recherche + refresh : masqué quand la liste est totalement
          vide (empty state global pris en charge plus bas). Sinon les contrôles
          n'ont rien à filtrer ni à rafraîchir. */}
      {(cgList.length > 0 || hasSearched) && (
        <Grid container item xs={12} sm={12} marginBottom={4} marginTop={0}>
          <Grid item xs={6} sm={5}>
            <TextField
              label="Rechercher"
              className="inputElement"
              variant="outlined"
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              size="small"
              sx={{
                '& .MuiInputLabel-root': {
                  top: '5px',
                },
                '& .MuiInputLabel-shrink': {
                  top: 0,
                },
              }}
            />
            <Button
              type="button"
              variant="contained"
              className="ActionButton"
              onClick={() => handleSearch(searchQuery, refetch)}
            >
              Rechercher
            </Button>
            {hasSearched && (
              <Button
                className="secondaryDealActionButton"
                sx={{ left: '10px', top: '-15px', height: '55px' }}
                onClick={() => resetSearch(refetch)}
              >
                Revenir à la liste
              </Button>
            )}
          </Grid>
          <Grid
            item
            xs={6}
            sm={3}
            sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', gap: 2 }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'flex-end',
                marginRight: 0,
                marginLeft: 0,
              }}
            >
              <Button
                type="button"
                variant="contained"
                className="ActionButton"
                startIcon={<SyncIcon />}
                onClick={handleRefresh}
              >
                Rafraichir la liste
              </Button>
            </Box>
          </Grid>
        </Grid>
      )}
      {!orgId || orgId <= 0 ? (
        <Typography variant="h6" sx={{ mt: 4, textAlign: 'center' }}>
          Il semblerait y avoir un problème dans la configuration de votre compte, veuillez nous contacter au 07 80 94
          97 37.
        </Typography>
      ) : isLoading || isLoadingCG ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <CircularProgress />
        </Box>
      ) : cgList.length === 0 && !hasSearched ? (
        // ── Empty state moderne ── (aucune CG du tout pour cette agence)
        // Le CTA navigue vers /form-cg : CGFormRouter dispatch automatiquement
        // vers /form-cgia v2 si le flag est activé pour l'agence, sinon il
        // tombe sur le CGForm legacy. Wording + emoji ✨ adaptés au flag pour
        // que l'agence comprenne ce qu'elle s'apprête à utiliser.
        <Paper
          sx={{
            mt: 6,
            mb: 6,
            mx: 'auto',
            maxWidth: 560,
            p: { xs: 4, sm: 6 },
            textAlign: 'center',
            borderRadius: 4,
            background: 'linear-gradient(180deg, #FFFBEB 0%, #FFFFFF 70%)',
            border: '1px solid #FDE68A',
            boxShadow: '0 8px 30px rgba(245, 194, 0, 0.10)',
          }}
        >
          <Typography sx={{ fontSize: 56, lineHeight: 1, mb: 2 }}>📋</Typography>
          <Typography
            sx={{
              fontSize: { xs: 22, sm: 26 },
              fontWeight: 900,
              color: '#1C1C1E',
              letterSpacing: '-0.01em',
              mb: 1,
            }}
          >
            Aucune carte grise pour le moment
          </Typography>
          <Typography
            sx={{
              fontSize: 15,
              color: '#666666',
              maxWidth: 420,
              mx: 'auto',
              mb: 4,
              lineHeight: 1.55,
            }}
          >
            {formCgiaEnabled
              ? "Démarrez votre première demande de changement de titulaire. Notre IA analyse les pièces, vérifie la cohérence avec l'ANTS et accompagne votre dossier de bout en bout."
              : 'Démarrez votre première demande de changement de titulaire. Skipcar prend en charge l’intégralité des démarches auprès de l’ANTS pour vous.'}
          </Typography>
          <Button
            variant="contained"
            onClick={() => {
              // Session FormCGIA vierge sans annuler l'analyse en cours
              // (consommé par FormCGIAv2 → handleNewSession).
              sessionStorage.setItem('form_cgia_new_session', '1');
              navigate('/form-cg');
            }}
            sx={{
              backgroundColor: '#F5C200',
              color: '#1C1C1E',
              fontWeight: 800,
              fontSize: 15,
              px: 4,
              py: 1.25,
              borderRadius: 999,
              textTransform: 'none',
              letterSpacing: '0.01em',
              boxShadow: '0 4px 14px rgba(245, 194, 0, 0.35)',
              '&:hover': {
                backgroundColor: '#D4A800',
                boxShadow: '0 6px 18px rgba(245, 194, 0, 0.45)',
              },
            }}
          >
            {formCgiaEnabled ? '✨ Démarrer une carte grise' : 'Démarrer une carte grise'}
          </Button>
          <Typography
            sx={{ fontSize: 12, color: '#888888', mt: 3, fontStyle: 'italic' }}
          >
            Besoin d'aide ? 07 80 94 97 37
          </Typography>
        </Paper>
      ) : (
        <>
          {!hasSearched && actionItems.length > 0 && (
            <ActionsRequiredAccordion items={actionItems} onItemClick={handleActionClick} />
          )}
          {hasSearched ? (
            <Paper sx={{ mb: 4, p: 2, backgroundColor: 'rgba(255, 237, 206, 1)', borderRadius: 2 }}>
              <CGTable title="Résultats de recherche" cgList={allCGFiltered} onRefresh={handleRefresh} />
            </Paper>
          ) : (
            <>
              <InFlightSection />

              {/* ── Action attendue côté agence (en tête) ──
                  Ordre fixe : ce sont les blocs où l'agence DOIT agir.
                  Tous masqués si vide pour épurer la liste. */}
              {cgComplementAction.length > 0 && (
                <Paper
                  sx={{
                    mb: 4,
                    p: 2,
                    backgroundColor: 'rgba(237, 233, 254, 1)',
                    borderRadius: 2,
                    borderLeft: '6px solid #6d28d9',
                  }}
                >
                  <CGTable
                    title="Complément à accepter ou à régler"
                    cgList={cgComplementAction}
                    onRefresh={handleRefresh}
                    scrollTarget={scrollTarget}
                  />
                </Paper>
              )}
              {cgPaymentAndDocs.length > 0 && (
                <Paper
                  sx={{
                    mb: 4,
                    p: 2,
                    backgroundColor: 'rgba(255, 192, 152, 1)',
                    borderRadius: 2,
                    borderLeft: '6px solid #C2410C',
                  }}
                >
                  <CGTable
                    title="En attente de paiement et de documents"
                    cgList={cgPaymentAndDocs}
                    onRefresh={handleRefresh}
                    scrollTarget={scrollTarget}
                  />
                </Paper>
              )}

              {cgPending.length > 0 && (
                <Paper sx={{ mb: 4, p: 2, backgroundColor: 'rgba(255, 237, 206, 1)', borderRadius: 2 }}>
                  <CGTable
                    title="En attente de paiement"
                    cgList={cgPending}
                    onRefresh={handleRefresh}
                    scrollTarget={scrollTarget}
                  />
                </Paper>
              )}

              {cgWaitingDocuments.length > 0 && (
                <Paper sx={{ mb: 4, p: 2, backgroundColor: 'rgba(255, 152, 0, 1)', borderRadius: 2 }}>
                  <CGTable
                    title="En attente de documents"
                    cgList={cgWaitingDocuments}
                    onRefresh={handleRefresh}
                    scrollTarget={scrollTarget}
                  />
                </Paper>
              )}

              {cgWaitingReply.length > 0 && (
                <Paper sx={{ mb: 4, p: 2, backgroundColor: 'rgba(255, 193, 7, 1)', borderRadius: 2 }}>
                  <CGTable
                    title="Skipcar attend votre réponse"
                    cgList={cgWaitingReply}
                    onRefresh={handleRefresh}
                    scrollTarget={scrollTarget}
                  />
                </Paper>
              )}

              {/* ── En cours côté Skipcar — l'agence patiente ── */}
              {cgReview.length > 0 && (
                <Paper sx={{ mb: 4, p: 2, backgroundColor: 'rgba(187, 222, 251, 1)', borderRadius: 2 }}>
                  <CGTable
                    title="En review Skipcar"
                    cgList={cgReview}
                    onRefresh={handleRefresh}
                    scrollTarget={scrollTarget}
                  />
                </Paper>
              )}

              {cgMisiv.length > 0 && (
                <Paper sx={{ mb: 4, p: 2, backgroundColor: 'rgba(249, 192, 122, 1)', borderRadius: 2 }}>
                  <CGTable
                    title="Démarches ANTS en cours"
                    cgList={cgMisiv}
                    onRefresh={handleRefresh}
                    scrollTarget={scrollTarget}
                  />
                </Paper>
              )}

              {/* ── Terminal ── */}
              {cgCompleted.length > 0 && (
                <Paper sx={{ mb: 4, p: 2, backgroundColor: 'rgba(151, 158, 210, 1)', borderRadius: 2 }}>
                  <CGTable
                    title="Procédure effectuée"
                    cgList={cgCompleted}
                    onRefresh={handleRefresh}
                    scrollTarget={scrollTarget}
                  />
                </Paper>
              )}

              {cgRejected.length > 0 && (
                <Paper sx={{ mb: 4, p: 2, backgroundColor: 'rgba(254, 226, 226, 1)', borderRadius: 2 }}>
                  <CGTable
                    title="Rejetées"
                    cgList={cgRejected}
                    onRefresh={handleRefresh}
                    scrollTarget={scrollTarget}
                  />
                </Paper>
              )}
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default AgencyCGList;
